import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  SeekerContractsService,
  ContractUnit,
  SeekerContractItem,
} from '../../../services/seeker-contracts.service';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';
import { LanguageService } from '../../../../../../core/services/language.service';

@Component({
  selector: 'app-my-contracts',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './my-contracts.html',
  styleUrl: './my-contracts.scss',
})
export class MyContracts implements OnInit {
  private service = inject(SeekerContractsService);
  private languageService = inject(LanguageService);

  contracts: SeekerContractItem[] = [];
  loading = true;
  error: string | null = null;

  ngOnInit(): void {
    this.service.getContracts().subscribe({
      next: (res) => {
        this.contracts = res.contracts || [];
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || err.message || 'Failed to load contracts';
        this.loading = false;
      },
    });
  }

  formatDate(d: string | undefined): string {
    if (!d) return '-';
    const locale = this.languageService.getCurrentLanguage() === 'ar' ? 'ar-EG' : 'en-GB';
    return new Date(d).toLocaleDateString(locale);
  }

  isContractUnit(unit: SeekerContractItem['unit']): unit is ContractUnit {
    return !!unit && typeof unit === 'object';
  }

  getUnitObject(unit: SeekerContractItem['unit']): ContractUnit | null {
    return this.isContractUnit(unit) ? unit : null;
  }

  getUnitTitle(unit: ContractUnit): string {
    const isArabic = this.languageService.getCurrentLanguage() === 'ar';
    return (isArabic ? unit.title_ar : unit.title_en) || (isArabic ? unit.title_en : unit.title_ar) || unit.unitNumber || '-';
  }

  typeTranslationKey(type: string | undefined): string {
    return `seekerDashboard.myContracts.${type || 'rental'}`;
  }

  statusTranslationKey(status: string | undefined): string {
    return `seekerDashboard.myContracts.${(status || '').toLowerCase()}`;
  }

  paymentMethodTranslationKey(method: string | undefined): string {
    return `seekerDashboard.myContracts.${method || 'cash'}`;
  }

  getContractDocumentUrl(contract: SeekerContractItem): string | null {
    const document = contract.contractDocument as { url?: string; secure_url?: string } | undefined;
    return document?.url || document?.secure_url || null;
  }

  statusClass(status: string): string {
    if (!status) return 'badge-neutral';
    const map: Record<string, string> = {
      draft: 'badge-warning',
      active: 'badge-success',
      completed: 'badge-primary',
      expired: 'badge-neutral',
      terminated: 'badge-danger',
      pending: 'badge-warning',
      under_review: 'badge-primary',
      pending_payment: 'badge-warning',
      terminated_early: 'badge-danger',
    };
    return map[status.toLowerCase()] ?? 'badge-neutral';
  }

  get activeCount(): number {
    return this.contracts.filter((c) => c.status === 'active').length;
  }
}
